import React, { useEffect, useRef, useState } from 'react';
import {
  useDispatch, useSelector, useStore, connect,
} from 'react-redux';
import msgpack from 'msgpack-lite';
import { Snackbar } from '@mui/material';
import { devicesActions, markersActions, sessionActions } from './store';
import { useEffectAsync } from './reactHelper';
import { snackBarDurationLongMs } from './common/util/duration';

import alarm from './resources/alarm.mp3';
import { eventsActions } from './store/events';
import useFeatures from './common/util/useFeatures';
import { useAttributePreference } from './common/util/preferences';
import { cleanIds } from './common/util/deviceData';

const logoutCode = 4000;

// The socket runs in tiered mode: the server sends no initial dump, and
// after the REST tiers have drawn the screen it streams a marker for every
// device that changes, plus full devices and positions only for the rows and
// status card the client has subscribed to. Each subscription message
// replaces the previous one entirely.
// A development build logs what goes out and a count of what comes in, so
// the first live runs of the tiered stream can be read from the console.
const debug = process.env.NODE_ENV !== 'production';

const sendSubscription = (socket, subscription) => {
  if (socket && socket.readyState === WebSocket.OPEN) {
    const message = {
      subscribe: {
        rows: cleanIds(subscription.rows),
        full: cleanIds(subscription.full),
      },
    };
    if (subscription.distanceUnit) {
      message.subscribe.distanceUnit = subscription.distanceUnit;
    }
    if (subscription.filter && Object.keys(subscription.filter).length) {
      message.subscribe.filter = subscription.filter;
    }
    socket.send(JSON.stringify(message));
    if (debug) {
      // eslint-disable-next-line no-console
      console.debug('[socket] subscribe', message.subscribe);
    }
  }
};

// A device record arrives only for a subscribed row or status card, or for
// a device that became visible after the page loaded (created in or moved
// into the company while the socket was open) or that came to match the
// subscribed filter. A record for an id outside the subscription is
// therefore an announcement, whatever the store may hold from before: a
// device loaded under an earlier filter, or dropped when it stopped matching,
// is still in the store. Batches already under way when the subscription
// changed can still carry the rows it dropped, so ids subscribed in the last
// few seconds do not count.
const recentSubscriptionMs = 10000;

const findNewDevices = (devices, subscription, recent) => {
  const now = Date.now();
  const subscribed = new Set([...(subscription.rows || []), ...(subscription.full || [])]);
  return devices
    .filter((device) => !subscribed.has(device.id) && !(now - (recent.get(device.id) || 0) < recentSubscriptionMs))
    .map((device) => device.id);
};

// Markers whose status differs from the one the index holds, and the first
// markers of devices it does not hold, which have never reported before.
// Only an unfiltered index holds every device that has reported, so only
// then can either be seen. A terminated device is not in the list then.
const findStatusChanges = (markers, markerState) => {
  if (markerState.filtered) {
    return [];
  }
  return markers
    .filter((marker) => {
      const known = markerState.items[marker.deviceId];
      return known ? known.status !== marker.status : marker.disabled !== true;
    })
    .map((marker) => marker.deviceId);
};

const SocketController = () => {
  const dispatch = useDispatch();
  const store = useStore();
  const authenticated = useSelector((state) => !!state.session.user);
  const subscription = useSelector((state) => state.session.subscription);
  const socketRef = useRef();
  const subscriptionRef = useRef(subscription);
  const recentRef = useRef(new Map());
  // Reconnect quickly after a drop and back off if the server stays away,
  // so a worker watching a live vehicle is not blind for a full minute.
  const reconnectDelayRef = useRef(5000);
  const [events, setEvents] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const soundEvents = useAttributePreference('soundEvents', '');
  const soundAlarms = useAttributePreference('soundAlarms', 'sos');
  const features = useFeatures();

  const connectSocket = () => {
    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const socket = new WebSocket(`${protocol}//${window.location.host}/api/socket?mode=tiered`);
    socket.binaryType = 'arraybuffer';
    socketRef.current = socket;

    socket.onopen = () => {
      reconnectDelayRef.current = 5000;
      dispatch(sessionActions.updateSocket(true));
      sendSubscription(socket, subscriptionRef.current);
    };

    socket.onclose = async (event) => {
      dispatch(sessionActions.updateSocket(false));
      if (event.code !== logoutCode) {
        // Nothing is replayed on reconnect. The main page refetches its
        // marker index when the socket is back, and the visible rows notice
        // their markers moved ahead of them and refresh themselves.
        const delay = reconnectDelayRef.current;
        reconnectDelayRef.current = Math.min(delay * 2, 60000);
        setTimeout(() => connectSocket(), delay);
      }
    };

    socket.onmessage = async (event) => {
      const data = msgpack.decode(new Uint8Array(event.data));
      const added = data.devices?.length
        ? findNewDevices(data.devices, subscriptionRef.current, recentRef.current)
        : [];
      const statusChanged = data.markers?.length
        ? findStatusChanges(data.markers, store.getState().markers)
        : [];
      if (debug) {
        // eslint-disable-next-line no-console
        console.debug('[socket] batch', {
          markers: data.markers?.length ?? 0,
          devices: data.devices?.map((device) => device.id) ?? [],
          positions: data.positions?.map((position) => position.deviceId) ?? [],
          removed: data.removed ?? [],
          added,
          statusChanged,
          counts: !!data.counts,
          events: data.events?.length ?? 0,
        });
      }
      // A new device joins the marker index before this batch's markers
      // are applied, so its dot is drawn even under a filter; the server
      // only announces it then if it matches. Without a filter a terminated
      // device stays off the map, as it would in a fresh index. The list
      // places it through the page hook.
      if (added.length) {
        const { filtered } = store.getState().markers;
        const mapped = data.devices
          .filter((device) => added.includes(device.id) && (filtered || !device.disabled))
          .map((device) => device.id);
        dispatch(markersActions.admit(mapped));
      }
      if (data.markers) {
        dispatch(markersActions.update(data.markers));
      }
      if (statusChanged.length) {
        dispatch(sessionActions.updateStatusChanges(statusChanged));
      }
      // Devices that stopped matching the subscribed filter leave the map
      // here and the list through the page hook.
      if (data.removed?.length) {
        dispatch(markersActions.remove(data.removed));
        dispatch(sessionActions.updateRemovedDevices(data.removed));
      }
      // Counts come as a list; the last set is the current one.
      if (data.counts?.length) {
        dispatch(sessionActions.updateCounts(data.counts[data.counts.length - 1]));
      }
      if (data.devices) {
        dispatch(devicesActions.update(data.devices));
      }
      if (added.length) {
        dispatch(sessionActions.updateAddedDevices(added));
      }
      if (data.positions) {
        dispatch(sessionActions.updatePositions(data.positions));
      }
      if (data.events) {
        if (!features.disableEvents) {
          dispatch(eventsActions.add(data.events));
        }
        setEvents(data.events);
      }
    };
  };

  useEffect(() => {
    const now = Date.now();
    const recent = recentRef.current;
    recent.forEach((time, id) => {
      if (now - time >= recentSubscriptionMs) {
        recent.delete(id);
      }
    });
    [...(subscriptionRef.current.rows || []), ...(subscriptionRef.current.full || [])]
      .forEach((id) => recent.set(id, now));
    subscriptionRef.current = subscription;
    sendSubscription(socketRef.current, subscription);
  }, [subscription]);

  useEffectAsync(async () => {
    if (authenticated) {
      // A previous login on this tab may have left another account's devices
      // behind; the tiers only ever add.
      dispatch(devicesActions.refresh([]));
      dispatch(markersActions.refresh({ markers: [] }));
      connectSocket();
      return () => {
        const socket = socketRef.current;
        if (socket) {
          socket.close(logoutCode);
        }
      };
    }
    return null;
  }, [authenticated]);

  useEffect(() => {
    setNotifications(events.map((event) => ({
      id: event.id,
      message: event.attributes.message,
      show: true,
    })));
  }, [events]);

  useEffect(() => {
    events.forEach((event) => {
      if (soundEvents.includes(event.type) || (event.type === 'alarm' && soundAlarms.includes(event.attributes.alarm))) {
        new Audio(alarm).play();
      }
    });
  }, [events, soundEvents, soundAlarms]);

  return (
    <>
      {notifications.map((notification) => (
        <Snackbar
          key={notification.id}
          open={notification.show}
          message={notification.message}
          autoHideDuration={snackBarDurationLongMs}
          onClose={() => setEvents(events.filter((e) => e.id !== notification.id))}
        />
      ))}
    </>
  );
};

export default connect()(SocketController);
